import * as Sentry from '@sentry/browser'
import React, { useEffect, useState } from 'react'
import { flowRight as compose } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'

import { Button, PromptGuard } from 'components'
import {
  initialForm,
  schemaForm,
  ProfileForm,
  IProfileFormProps,
  findUserById,
  BREADCRUMBS_EDIT,
} from 'features/Profile'
import { updateStaff } from 'features/Staff'
import { withBackOfficeCms } from 'helpers/hocs'
import { getImage, getPhoneFormatter } from 'helpers/common'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'

import type { ISchemaFormStaffFormPayload } from 'features/Staff'
import { loaderState, userProfileState } from 'recoils/atoms'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'

const ProfileEdit = (): JSX.Element => {
  const { t } = useTranslation(['common', 'layout'])

  const { profileId } = useRecoilValue(userProfileState)

  const resetUserProfile = useResetRecoilState(userProfileState)

  const [profile, setProfile] = useState<IProfileFormProps>(initialForm)
  const setLoader = useSetRecoilState(loaderState)

  const base = 'con-profile'

  const methods = useForm<IProfileFormProps>({
    defaultValues: initialForm,
    resolver: yupResolver(schemaForm),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })
  const [shouldPrompt, setShouldPrompt] =
    usePromptForm<IProfileFormProps>(methods)
  const [loading] = useFormSubmitLoader<IProfileFormProps>(methods)

  useBreadcrumbs(BREADCRUMBS_EDIT)

  useEffect(() => {
    if (profileId) {
      fetchUser(profileId)
    }
  }, [profileId])

  useEffect(() => {
    if (!profile) {
      return
    }

    methods.reset({
      ...initialForm,
      imagePath: getImage(profile.imagePath) || '',
      code: profile.code || '',
      nickname: profile.nickname || '',
      firstName: profile.firstName,
      lastName: profile.lastName,
      countryCode: profile.countryCode || '+66',
      phoneNumber: profile.phoneNumber
        ? getPhoneFormatter(profile.phoneNumber)
        : '',
      email: profile.email || '',
      role: profile.role || '',
    })
  }, [profile])

  const fetchUser = async (id: string) => {
    setLoader(true)

    const { data } = await findUserById(id)
    const profileData = data as IProfileFormProps

    if (profileData) {
      setProfile(profileData)
      setLoader(false)
    }
  }

  const onSubmit = async (value: ISchemaFormStaffFormPayload) => {
    const newValue = {
      ...value,
      ...(value.phoneNumber && {
        phoneNumber: getPhoneFormatter(value.phoneNumber),
      }),
    }

    delete newValue.role

    try {
      setShouldPrompt(false)
      await updateStaff(profileId, newValue)

      resetUserProfile()
      history.go(0)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <PromptGuard when={shouldPrompt} />
      <form className={base} onSubmit={methods.handleSubmit(onSubmit)}>
        <ProfileForm />
        <div className='container__form__actions'>
          <Button variants='ternary' onClick={() => history.back()}>
            {t('common:cancel.button')}
          </Button>
          <Button type='submit' isLoading={loading}>
            {t('common:save.button')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default compose(withBackOfficeCms)(ProfileEdit)
